import { SupportedLocale as SupportedLocaleGQL, User } from 'generated/graphql';
import {
  formatPersonalName,
  getSupportedLocaleMetadata,
  isGenderedLocale as isGenderedSupportedLocale,
  SupportedLocale,
} from '@sz/l10n';

export const getLocale = (locale: SupportedLocaleGQL) => {
  return SupportedLocale[locale as keyof typeof SupportedLocale];
};

export const getFullNameFromLanguage = ({
  firstName,
  lastName,
  preferredLocale,
}: User): string => {
  return formatPersonalName(
    {
      givenName: firstName,
      familyName: lastName,
    },
    getLocale(preferredLocale.locale)
  );
};

export const getInitialsInOrderFromLanguage = (user: User): string => {
  const fullName = getFullNameFromLanguage(user);

  if (fullName.startsWith(user.firstName)) {
    return user.firstName[0].toUpperCase() + user.lastName[0].toUpperCase();
  }

  return user.lastName[0].toUpperCase() + user.firstName[0].toUpperCase();
};

export const getLocalisationLanguage = (locale: SupportedLocaleGQL) => {
  const userLocale = getLocale(locale);

  //Set english as default language and otherwise use locale name provided from the BE
  return userLocale === SupportedLocale.ENGLISH ? 'en' : userLocale;
};

/**
 * Reverse of getLocalisationLanguage: maps an i18next language code used by
 * the LanguagePicker (e.g. 'en', 'es-419', 'zh-TW') to the GQL SupportedLocale
 * enum value, or undefined if the code is not a backend-supported locale.
 */
export const getSupportedLocaleFromLanguageCode = (
  language: string
): SupportedLocaleGQL | undefined =>
  (Object.values(SupportedLocaleGQL) as SupportedLocaleGQL[]).find(
    locale => getLocalisationLanguage(locale) === language
  );

export const getLanguageNameFromLocale = (locale: SupportedLocaleGQL) => {
  return getSupportedLocaleMetadata(getLocale(locale)).name.english;
};

/**
 * Whether the locale's grammar requires the AI interviewer to use masculine or
 * feminine forms when addressing the candidate.
 */
export const isGenderedLocale = (locale: SupportedLocaleGQL): boolean =>
  isGenderedSupportedLocale(getLocale(locale));
